import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { isAppAuthSelector, isBillingAdminSelector } from 'pricing-pages-ui/selectors/AuthSelectors';
import { useQueryParamFromLocation } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { makeLoadable } from '../components/common/LoadableWrapper';
import { MISMATCHED_RESPONSES, MISMATCHED_SEATS_GATING } from '../constants/ErrorCauses';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useBulkPurchaseOptions } from '../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode, usePricingPageCurrencies } from '../hooks/useCurrencies';
import { useFeaturesWithDefaults } from '../hooks/useFeatures';
import ErrorPage from '../pages/ErrorPage';
import { useAppSelector } from '../reducers';
import { areGatesPopulatedSelector } from '../selectors/GatingSelectors';
import { hasPublicMetaDataSucceededSelector } from '../selectors/LoadingStateSelectors';
import { hasMisMatchedProductsAndPromotionsSelector, hasMismatchedGatingForSeatsSelector } from '../selectors/ProductsSelectors';
import { isHubSpotDotComAuthedUserRedirect } from '../utils/appUtils';
import { Metrics } from '../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
const LoadableBaseLayout = makeLoadable({
  loader: () => import( /* webpackChunkName: "BaseLayout" */'pricing-pages-ui/layouts/BaseLayout')
});
const AppLayout = ({
  children,
  location
}) => {
  const isBillingAdmin = useSelector(isBillingAdminSelector);
  const isAppAuth = useSelector(isAppAuthSelector);
  const currencyCode = useDisplayedCurrencyCode();
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);
  const isRedirect = isHubSpotDotComAuthedUserRedirect();
  useEffect(() => {
    getUsageTracker().setProperties({
      isBillingAdmin,
      upgradeSource,
      inAppOrPublic: isAppAuth ? 'in-app' : 'public',
      activeCurrencyCode: currencyCode,
      isDotComAuthedRedirect: isAppAuth && isRedirect
    });
    if (isRedirect) {
      Metrics.counter('redirected-from-dotcom').increment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePricingPageCurrencies({
    isAppAuth
  });
  useBulkPurchaseOptions();

  // Legacy bulk fetch to prime the cache - this bulk query is for default/fallback copies for features, used in TranslatedFeature, in the case where the features/by-api-name does not contain the copy
  useFeaturesWithDefaults();
  const isAppReady = useGlobalLoading();
  const hasMisMatchedProductsAndPromotions = useAppSelector(state => {
    if (!isAppReady) return false;
    return hasMisMatchedProductsAndPromotionsSelector(state);
  });
  const hasMismatchedGatingForSeats = useAppSelector(state => {
    const hasPublicMetaDataSucceeded = hasPublicMetaDataSucceededSelector(state);
    const areGatesPopulated = areGatesPopulatedSelector(state);
    const isPublicLoading = !isAppAuth && !hasPublicMetaDataSucceeded || !areGatesPopulated;
    const isLoading = !isAppReady || isPublicLoading;
    if (isLoading) {
      return false;
    }
    return hasMismatchedGatingForSeatsSelector(state);
  });
  const ref = useRef(null);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    var _ref$current;
    (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView();
  }, [pathname]);
  if (hasMisMatchedProductsAndPromotions) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: MISMATCHED_RESPONSES
    });
  }
  if (hasMismatchedGatingForSeats && hasMismatchedGatingForSeats.mismatched) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: MISMATCHED_SEATS_GATING,
      additionalErrorLogging: hasMismatchedGatingForSeats
    });
  }
  const isRedirectedFromDotCom = isHubSpotDotComAuthedUserRedirect();
  if (isAppAuth && !isRedirectedFromDotCom) {
    return /*#__PURE__*/_jsx("div", {
      ref: ref,
      children: children
    });
  }

  /**
   * If we came from public hubspot.com/pricing render the dot com marketing nav.
   * This represents authenticated users who go to the website pricing pages
   * We want to preserve the dot com nav since otherwise it would lead to a weird
   * experience where they'd lose their nav and couldn't go back without using the
   * browser back button
   */
  return /*#__PURE__*/_jsx(LoadableBaseLayout, {
    isLoggedIn: isAppAuth,
    pathname: location.pathname,
    children: /*#__PURE__*/_jsx("div", {
      ref: ref,
      children: children
    })
  });
};
export default withRouter(AppLayout);